import {keepPreviousData, queryOptions} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {SavedView, SavedViewsResponse} from '../components/views/types'

export const SAVED_VIEWS_QUERY_KEY = 'repo-saved-views'
export const SAVED_VIEW_QUERY_KEY = 'repo-saved-view'

// Prefers `{error: {message}}` JSON, then raw text, then `fallback`.
export async function parseFetchErrorMessage(response: Response, fallback: string): Promise<string> {
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    try {
      const payload = (await response.json()) as {error?: {message?: string}} | null
      if (payload?.error?.message) {
        return payload.error.message
      }
    } catch {
      // not JSON — fall through
    }
    return fallback
  }
  try {
    const text = await response.text()
    if (text) {
      return text
    }
  } catch {
    // ignore
  }
  return fallback
}

// No pagination: the server caps a repo at 25 saved views, so we fetch them all in one shot.
export function savedViewsQueryOptions({owner, repo}: {owner: string; repo: string}) {
  return queryOptions({
    queryKey: [SAVED_VIEWS_QUERY_KEY, owner, repo] as const,
    placeholderData: keepPreviousData,
    queryFn: async ({signal}) => {
      const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/views.json`, {signal})
      if (!response.ok) {
        throw new Error(await parseFetchErrorMessage(response, `Failed to fetch saved views (${response.status})`))
      }
      return (await response.json()) as SavedViewsResponse
    },
  })
}

export function savedViewQueryOptions({owner, repo, id}: {owner: string; repo: string; id: string}) {
  return queryOptions({
    queryKey: [SAVED_VIEW_QUERY_KEY, owner, repo, id] as const,
    queryFn: async ({signal}) => {
      const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/views/${id}.json`, {signal})
      if (!response.ok) {
        throw new Error(await parseFetchErrorMessage(response, `Failed to fetch view (${response.status})`))
      }
      return (await response.json()) as {savedView: SavedView}
    },
  })
}
